import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { QueryClient } from '@tanstack/react-query';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

// Utils
import getServerSideTranslations from '../../src/utils/server-side';
import getServerDefaultLayout from '../../src/utils/getServerDefaultLayout';
import { replacePageSlug } from '../../src/utils/pageSlug';

// Services
import { getContentTranslationProxy } from '../../src/services/content-translations';

// Components
import PageTransition from '../../components/templates/PageTransition/PageTransition';
import PageLayout from '../../components/templates/PageLayout/PageLayout';
import SiteMapSection from '../../components/atoms/SiteMapSection/SiteMapSection';
import TitleBlock from '../../components/atoms/TitleBlock/TitleBlock';
import OneColumnBlock from '../../components/atoms/OneColumnBlock/OneColumnBlock';
import SiteMapLayout from '../../components/atoms/SiteMapLayout/SiteMapLayout';

// Constants
import {
  BANNED_PAGE_SLUGS,
  BLOG_SLUG,
  CONTACT_SLUG,
  LOCALE_FR,
  CO_LIVING,
  RESTORATION,
  CO_WORKING,
  SHORT_STAY,
  SLUG_FR_HOTEL,
  SLUG_EN_RESTORATION,
  URL_COLIVING_RESIDENCE_PAGES,
  URL_SEARCH_PAGES,
  SLUG_FR_RESTORATION,
  URL_COWORKINGS_PAGES,
  MEDIUM_LONG_STAY,
} from '../../src/constants';

function Index({
  locale, header, footer, pageResidences, pages, articles,
}) {
  const { t } = useTranslation();

  const colivingResidence = useMemo(
    () => {
      if (!pageResidences?.length) return [];
      return pageResidences.reduce((acc, item) => {
        if (item?.data?.activity === CO_LIVING || item?.data?.activity === MEDIUM_LONG_STAY) {
          const slug = `${replacePageSlug(item?.data?.slug, locale)}`;
          const residence = { ...item, data: { ...item?.data, slug } };
          return acc.concat(residence);
        }
        return acc;
      }, []);
    },
    [pageResidences?.data],
  );

  const hotels = useMemo(
    () => {
      if (!pageResidences?.length) return [];
      return pageResidences.reduce((acc, item) => {
        if (item?.data?.activity === SHORT_STAY) {
          const slug = `${replacePageSlug(item?.data?.slug, locale)}`;
          const residence = { ...item, data: { ...item?.data, slug } };
          return acc.concat(residence);
        }
        return acc;
      }, []);
    },
    [pageResidences?.data],
  );

  const restaurants = useMemo(
    () => {
      if (!pageResidences?.length) return [];
      return pageResidences.reduce((acc, item) => {
        if (item?.data?.activity === RESTORATION) {
          const slug = `${replacePageSlug(item?.data?.slug, locale)}`;
          const residence = { ...item, data: { ...item?.data, slug } };
          return acc.concat(residence);
        }
        return acc;
      }, []);
    },
    [pageResidences?.data],
  );

  const coworkings = useMemo(
    () => {
      if (!pageResidences?.length) return [];
      return pageResidences.reduce((acc, item) => {
        if (item?.data?.activity === CO_WORKING) {
          const slug = `${replacePageSlug(item?.data?.slug, locale)}`;
          const residence = { ...item, data: { ...item?.data, slug } };
          return acc.concat(residence);
        }
        return acc;
      }, []);
    },
    [pageResidences?.data],
  );

  const pagesData = useMemo(
    () => {
      if (!pages?.length) return [];
      return pages.reduce((acc, item) => {
        const slug = `${replacePageSlug(item?.data?.slug, locale)}`;
        if (BANNED_PAGE_SLUGS.includes(slug)) return acc;
        const page = { ...item, data: { ...item?.data, slug } };
        return acc.concat(page);
      }, []);
    },
    [pages],
  );

  const articlesData = useMemo(
    () => {
      if (!articles?.length) return [];
      return articles.reduce((acc, item) => {
        const category = item?.data?.category?.[0]?.data?.slug;
        const slug = `${category?.replace('slug:', '')}/${item?.canonicalSlug}`;
        return acc.concat({ ...item, data: { ...item?.data, slug } });
      }, []);
    },
    [articles],
  );

  return (
    <>
      <Head>
        <title>{t('sitemap.metaTitle')}</title>
        <meta name="description" content={t('sitemap.metaTitle')} />
      </Head>

      <PageLayout
        header={header?.[0]?.data}
        footer={footer?.[0]?.data}
      >
        <PageTransition>

          <OneColumnBlock>
            <TitleBlock theme="light" title={t('sitemap.title')} />
          </OneColumnBlock>

          <SiteMapLayout>
            <SiteMapSection name={t('sitemap.home')} link="/" />

            {pagesData?.map(({ data }) => (
              <SiteMapSection key={data?.metaTitle} name={data?.metaTitle} link={data?.slug?.replace('slug:', '')} />
            ))}

            <SiteMapSection name={t('sitemap.contact')} link={CONTACT_SLUG} />

            <SiteMapSection name={t('sitemap.colivingResidence')} link={URL_SEARCH_PAGES[locale]}>
              {colivingResidence?.length ? (
                <ul>
                  {colivingResidence?.map(({ data }) => {
                    const residenceName = data?.residence?.data?.nameLong;
                    const slug = `${URL_COLIVING_RESIDENCE_PAGES[locale]}/${data?.slug?.replace('slug:', '')}`;
                    return (
                      <li key={data?.slug}>
                        <Link href={slug}>
                          {residenceName}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </SiteMapSection>

            <SiteMapSection name={t('sitemap.hotels')} link={SLUG_FR_HOTEL}>
              {hotels?.length ? (
                <ul>
                  {hotels?.map(({ data }) => {
                    const residenceName = data?.residence?.data?.nameLong;
                    const slug = `${SLUG_FR_HOTEL}/${data?.slug?.replace('slug:', '')}`;
                    return (
                      <li key={data?.slug}>
                        <Link href={slug}>
                          {residenceName}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </SiteMapSection>

            <SiteMapSection name={t('sitemap.restaurants')} link={SLUG_FR_RESTORATION}>
              {restaurants?.length ? (
                <ul>
                  {restaurants?.map(({ data }) => {
                    const residenceName = data?.residence?.data?.nameLong;
                    const slug = `${SLUG_EN_RESTORATION}/${data?.slug?.replace('slug:', '')}`;
                    return (
                      <li key={data?.slug}>
                        <Link href={slug}>
                          {residenceName}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </SiteMapSection>

            <SiteMapSection name={t('sitemap.coworkings')} link={URL_COWORKINGS_PAGES[locale]}>
              {coworkings?.length ? (
                <ul>
                  {coworkings?.map(({ data }) => {
                    const residenceName = data?.residence?.data?.nameLong;
                    const slug = `${URL_COWORKINGS_PAGES[locale]}/${data?.slug?.replace('slug:', '')}`;
                    return (
                      <li key={data?.slug}>
                        <Link href={slug}>
                          {residenceName}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </SiteMapSection>

            {locale === LOCALE_FR ? (
              <SiteMapSection name={t('sitemap.articles')} link={`/${BLOG_SLUG}`}>
                {articlesData?.length ? (
                  <ul>
                    {articlesData?.map(({ data }) => (
                      <li key={data?.slug}>
                        <Link href={`/${BLOG_SLUG}/${data?.slug}`}>
                          {data?.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </SiteMapSection>
            ) : null}

          </SiteMapLayout>
        </PageTransition>
      </PageLayout>
    </>
  );
}

Index.propTypes = {
  locale: PropTypes.string.isRequired,
  header: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,
  footer: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,
  pageResidences: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  articles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export async function getServerSideProps({ locale }) {
  const translations = await getServerSideTranslations(locale);

  const queryClient = new QueryClient();
  const { header, footer } = await getServerDefaultLayout(queryClient, locale);

  let pageResidences;
  let pages;
  let articles;

  try {
    pageResidences = await queryClient.fetchQuery(
      ['page', { contentTypeId: 'page-residence', locale }],
      () => getContentTranslationProxy({ contentTypeId: 'page-residence', pageSize: 9999 }, locale)
        .then(async (res) => {
          if (res?.data?.length) { return res?.data; }
          return res;
        }),
    );
  } catch (error) {
    articles = {};
  }

  try {
    pages = await queryClient.fetchQuery(
      ['page', { contentTypeId: 'page', locale }],
      () => getContentTranslationProxy({ contentTypeId: 'page', pageSize: 9999 }, locale)
        .then(async (res) => {
          if (res?.data?.length) {
            return res?.data;
          }
          return res;
        }),
    );
  } catch (error) {
    pages = {};
  }

  try {
    articles = await queryClient.fetchQuery(
      ['page', { contentTypeId: 'article', locale }],
      () => getContentTranslationProxy({ contentTypeId: 'article', pageSize: 9999 }, locale)
        .then(async (res) => {
          if (res?.data?.length) {
            return res?.data;
          }
          return res;
        }),
    );
  } catch (error) {
    articles = {};
  }

  return {
    props: {
      header,
      footer,
      pageResidences,
      pages,
      articles,
      ...translations,
    },
  };
}

export default Index;
